import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {IconBox} from '../../../../../../../../../src/components/common/iconBox/iconBox';
import {
  CrmSyncCardDefaultFieldIdsEnum,
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncModel,
  CrmSyncSourcesEnum,
  CrmSyncStatusesEnum,
} from '../../../../../../../../../src/models/api/crmSyncModel';
import {Trans} from '../../../../../../../../shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../../../../shared/helpers/src/react/reactHelpers';
import {Icon} from '../../../../../../../../shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../../../shared/ui/src/styles/fontStyles';
import {UserTheme, useUserTheme} from '../../../../../../../../shared/ui/src/theme/userThemeProvider';
import {CrmSyncsForm, CrmSyncsFormModel} from '../crmSyncsForm';
import {CrmSyncLoginContainer} from '../crmSyncsLogin/crmSyncsLoginContainer';
import {CrmSyncOptionsEnum} from '../crmSyncsOptions/crmSyncsOptions';
import {CrmSyncsPageConfig, useCrmPageConfig} from '../crmSyncsPageConfig/crmSyncsPageConfig';
import {CrmSyncsLoadingForm} from './crmSyncsLoadingForm';
import {CrmSyncsPlaceholderForm} from './crmSyncsPlaceholderForm';
import {CrmSyncsSettingsContainerProps} from './crmSyncsSettingsContainer';
import {CrmSyncsUpsellCard} from './crmSyncsUpsellCard';

/*
 * Types.
 */

export interface CrmSyncSettingsFormModel {
  account?: CrmSyncsFormModel;
  card?: CrmSyncsFormModel;
  isFormInvalid?: boolean;
}

/*
 * Styles.
 */

const StyledErrorBannerDiv = styled.div`
  background: ${(p) => p.theme.palette.red.shade20};
  padding: 9px 10px;
  border-radius: 8px;
  gap: 8px;
  display: flex;
  width: 600px;
  font-size: ${fontSizes.small};
`;

const StyledIconBox = styled(IconBox)`
  width: 600px;
`;

const StyledErrorBoxDiv = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledErrorTitleDiv = styled.div`
  font-weight: ${fontWeights.bold};
  font-size: 13px;
  color: ${(p) => p.theme.greys.shade90};
  margin-bottom: 8px;
`;

const StyledErrorDescriptionDiv = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme.greys.shade80};
`;

/*
 * Component.
 */

export const CrmSyncsSettings: FC<CrmSyncsSettingsContainerProps> = (props) => {
  const {
    isAdmin,
    isConnected,
    isSavingOrSyncing,
    source,
    username,
    accountCrmSync,
    hasCrmSyncFeature,
    hasExpiredCredentials,
    initialFormState,
    onChange,
  } = props;
  const config = useCrmPageConfig(source);
  const [settingsFormState, setSettingsFormState] = useState<CrmSyncSettingsFormModel | undefined>(
    initialFormState,
  );

  const theme = useUserTheme();

  const onFormChange = (updatedFormState: CrmSyncSettingsFormModel) => {
    const isFormInvalid = computeIsFormInvalid(
      updatedFormState,
      computeIsEnabled(updatedFormState?.account, accountCrmSync),
    );
    setSettingsFormState({...updatedFormState, isFormInvalid});
    onChange({...updatedFormState, isFormInvalid});
  };

  if (!hasCrmSyncFeature) {
    return renderUpsellForm(isAdmin, source);
  }

  return (
    <>
      {!isAdmin && renderAdminWarning(theme)}
      <CrmSyncLoginContainer
        hasExpiredCredentials={hasExpiredCredentials}
        isConnected={isConnected}
        source={source}
        username={username}
        isDisabled={isSavingOrSyncing}
      />
      {renderForms(props, settingsFormState, onFormChange, config)}
      {maybeRenderHandleRequiredErrorBanner(theme, settingsFormState?.card)}
    </>
  );
};

/*
 * Helpers.
 */

function renderUpsellForm(isAdmin: boolean, source: CrmSyncSourcesEnum) {
  return (
    <>
      <CrmSyncsUpsellCard isAdmin={isAdmin} source={source} />
      <CrmSyncsPlaceholderForm source={source} />
    </>
  );
}

function renderAdminWarning(theme: UserTheme) {
  return (
    <StyledIconBox
      iconName="warningFilledSmall"
      backgroundColor={theme.palette.orange.shade20}
      iconColor={theme.palette.orange.shade40}
    >
      <StyledErrorBoxDiv>
        <StyledErrorTitleDiv>
          <Trans id="crm-syncs-page-admin-privileges-required-title">Administrator privileges required</Trans>
        </StyledErrorTitleDiv>
        <StyledErrorDescriptionDiv>
          <Trans id="crm-syncs-page-admin-privileges-required-description">
            Reach out to your admin to make changes on this page
          </Trans>
        </StyledErrorDescriptionDiv>
      </StyledErrorBoxDiv>
    </StyledIconBox>
  );
}

function renderForms(
  props: CrmSyncsSettingsContainerProps,
  settingsFormState: CrmSyncSettingsFormModel | undefined,
  onFormChange: HandlerOf<CrmSyncSettingsFormModel>,
  config: CrmSyncsPageConfig,
) {
  const {
    isAdmin,
    isConnected,
    isCrmSyncLoading,
    isSavingOrSyncing,
    source,
    panelId,
    accountCrmSync,
    cardCrmSync,
    hasExpiredCredentials,
    onViewErrorsClick,
    setHasExpiredCredentials,
  } = props;
  const {account, card} = config;

  if (isCrmSyncLoading) {
    return <CrmSyncsLoadingForm source={source} />;
  }

  const showPlaceholderSection = computeShowPlaceholder(config, accountCrmSync, cardCrmSync);
  if (showPlaceholderSection) {
    return <CrmSyncsPlaceholderForm source={source} />;
  }

  const isAccountsEnabled = computeIsEnabled(settingsFormState?.account, accountCrmSync);
  return (
    <>
      {account && accountCrmSync && (
        <CrmSyncsForm
          /* Remount the form if we load a different source or account sync */
          key={`${source}-${accountCrmSync.id}`}
          resourceType={CrmSyncIntegrationResourceTypesEnum.ACCOUNT}
          panelId={panelId}
          source={source}
          isAdmin={isAdmin}
          isConnected={isConnected}
          isSavingOrSyncing={isSavingOrSyncing}
          hasExpiredCredentials={hasExpiredCredentials}
          crmSync={accountCrmSync}
          config={account}
          onViewErrorsClick={onViewErrorsClick}
          initialFormState={settingsFormState?.account}
          onChange={(updatedAccountsFormState) => {
            onFormChange({...settingsFormState, account: updatedAccountsFormState});
          }}
          setHasExpiredCredentials={setHasExpiredCredentials}
          isLastForm={!card}
        />
      )}
      {card && cardCrmSync && (
        <CrmSyncsForm
          key={`${source}-${cardCrmSync.id}`}
          resourceType={CrmSyncIntegrationResourceTypesEnum.CARD}
          panelId={panelId}
          source={source}
          isAdmin={isAdmin}
          isConnected={isConnected}
          isSavingOrSyncing={isSavingOrSyncing}
          disabledOptionType={!isAccountsEnabled ? CrmSyncOptionsEnum.ACCOUNTS : undefined}
          hasExpiredCredentials={hasExpiredCredentials}
          crmSync={cardCrmSync}
          config={card}
          initialFormState={settingsFormState?.card}
          onViewErrorsClick={onViewErrorsClick}
          onChange={(updatedCardsFormState) => {
            onFormChange({...settingsFormState, card: updatedCardsFormState});
          }}
          setHasExpiredCredentials={setHasExpiredCredentials}
          isLastForm
        />
      )}
    </>
  );
}

function maybeRenderHandleRequiredErrorBanner(theme: UserTheme, formState?: CrmSyncsFormModel) {
  if (isCardMappingMissingHandle(formState)) {
    return (
      <StyledErrorBannerDiv>
        <Icon name="warningFilledSmall" color={theme.palette.red.shade50} />
        <Trans id="crm-syncs-page-contact-handle-required-error">
          At least one handle field required (email, phone number, etc)
        </Trans>
      </StyledErrorBannerDiv>
    );
  }
  return null;
}

function isCardMappingMissingHandle(formState?: CrmSyncsFormModel) {
  if (!formState) {
    return false;
  }
  const hasHandle = formState?.mappedFields.some(
    (field) =>
      field.customFieldId === CrmSyncCardDefaultFieldIdsEnum.EMAIL ||
      field.customFieldId === CrmSyncCardDefaultFieldIdsEnum.PHONE,
  );
  return !hasHandle;
}

function computeIsFormInvalid(settingsForm: CrmSyncSettingsFormModel, isAccountsEnabled: boolean) {
  const isAccountsOptionInvalid =
    !isAccountsEnabled && settingsForm.card?.selectedListOption.type === CrmSyncOptionsEnum.ACCOUNTS;
  return (
    isAccountsOptionInvalid ||
    isCardMappingMissingHandle(settingsForm.card) ||
    settingsForm.card?.isInvalid ||
    settingsForm.account?.isInvalid
  );
}

function computeIsEnabled(form?: CrmSyncsFormModel, crmSync?: CrmSyncModel) {
  return form ? form.isEnabled : crmSync?.status === CrmSyncStatusesEnum.ENABLED;
}

function computeShowPlaceholder(
  {account, card}: CrmSyncsPageConfig,
  accountCrmSync?: CrmSyncModel,
  cardCrmSync?: CrmSyncModel,
) {
  // if the config describes both account and card syncs, we need to have fetched both syncs or we show a placeholder
  if (account && card) {
    return !accountCrmSync || !cardCrmSync;
  }

  if (card) {
    return !cardCrmSync;
  }

  if (account) {
    return !accountCrmSync;
  }

  return true;
}
